import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import {
  MessageSquareWarning,
  Search,
  Eye,
  Loader2,
  X,
  CheckCircle,
  Clock,
  AlertTriangle,
  Ban
} from 'lucide-react';

const STATUS_OPCOES = [
  { id: 'todos', label: 'Todos' },
  { id: 'aberta', label: 'Abertas' },
  { id: 'em_andamento', label: 'Em andamento' },
  { id: 'resolvida', label: 'Resolvidas' },
  { id: 'descartada', label: 'Descartadas' }
];

function formatDate(val) {
  if (!val) return '—';
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('pt-BR');
  } catch {
    return '—';
  }
}

function StatusBadge({ status }) {
  const map = {
    aberta: { label: 'Aberta', className: 'bg-amber-100 text-amber-900', Icon: AlertTriangle },
    em_andamento: { label: 'Em andamento', className: 'bg-blue-100 text-blue-900', Icon: Clock },
    resolvida: { label: 'Resolvida', className: 'bg-emerald-100 text-emerald-900', Icon: CheckCircle },
    descartada: { label: 'Descartada', className: 'bg-gray-100 text-gray-700', Icon: Ban }
  };
  const cfg = map[status] || { label: status || '—', className: 'bg-gray-100 text-gray-700', Icon: Clock };
  const Icon = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.className}`}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}

const SolicitacoesSistemaPage = () => {
  const { isRole } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState(null);
  const [notas, setNotas] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const listSolicitacoesSistema = httpsCallable(functions, 'listSolicitacoesSistema');
      const result = await listSolicitacoesSistema({
        page: 1,
        limit: 100,
        status: statusFilter === 'todos' ? undefined : statusFilter,
        search: searchTerm.trim() || undefined
      });
      if (result.data?.success) {
        setItems(result.data.data?.items || []);
      } else {
        throw new Error(result.data?.message || 'Erro ao carregar');
      }
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Erro ao carregar solicitações');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm]);

  useEffect(() => {
    load();
  }, [load]);

  const abrir = (item) => {
    setSelected(item);
    setNotas(item.notas_tratamento || '');
    setMsg('');
  };

  const atualizar = async (novoStatus) => {
    if (!selected) return;
    setSalvando(true);
    setMsg('');
    try {
      const updateSolicitacaoSistema = httpsCallable(functions, 'updateSolicitacaoSistema');
      const result = await updateSolicitacaoSistema({
        solicitacao_id: selected.id,
        status: novoStatus || selected.status,
        notas_tratamento: notas
      });
      if (!result.data?.success) {
        throw new Error('Falha ao atualizar');
      }
      const updated = result.data.data;
      setSelected(updated);
      setMsg('Atualizado com sucesso.');
      await load();
    } catch (e) {
      setMsg(e?.message || 'Erro ao atualizar');
    } finally {
      setSalvando(false);
    }
  };

  if (!isRole('superadmin')) {
    return (
      <div className="p-6 text-center text-gray-600">Acesso restrito ao superadmin.</div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <MessageSquareWarning className="h-7 w-7 text-amber-600" />
          Solicitações do sistema
        </h1>
        <p className="text-gray-600 mt-1 text-sm">
          Erros e ajustes reportados pelos usuários, com a página de origem.
        </p>
      </div>

      <div className="bg-white border rounded-lg p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs font-medium text-gray-500 mb-1">Buscar</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm"
              placeholder="Texto, página, usuário…"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            {STATUS_OPCOES.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={load}
          className="px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800"
        >
          Atualizar
        </button>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm">{error}</div>
      )}

      <div className="bg-white border rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin" />
            Carregando…
          </div>
        ) : items.length === 0 ? (
          <p className="text-center text-gray-500 py-12 text-sm">Nenhuma solicitação encontrada.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-4 py-3">Data</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Página</th>
                  <th className="px-4 py-3">Usuário</th>
                  <th className="px-4 py-3">Mensagem</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">{formatDate(item.criado_em)}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{item.pagina_titulo || '—'}</p>
                      <p className="text-xs font-mono text-gray-500">{item.pagina_path}</p>
                      {item.modal_titulo ? (
                        <p className="text-xs text-amber-800 mt-0.5">Modal: {item.modal_titulo}</p>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-900">{item.user_nome}</p>
                      <p className="text-xs text-gray-500">{item.empresa_nome || item.user_funcao || '—'}</p>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <p className="truncate text-gray-700">{item.mensagem}</p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => abrir(item)}
                        className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-900 text-xs font-medium"
                      >
                        <Eye className="h-4 w-4" />
                        Abrir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h2 className="font-semibold text-gray-900">Detalhe da solicitação</h2>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="p-1 rounded hover:bg-gray-100"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto space-y-3 text-sm flex-1">
              <div className="flex items-center justify-between gap-2">
                <StatusBadge status={selected.status} />
                <span className="text-xs text-gray-500">{formatDate(selected.criado_em)}</span>
              </div>
              <div className="rounded-lg bg-gray-50 border px-3 py-2 text-xs space-y-1">
                <p>
                  <span className="font-medium">Página:</span> {selected.pagina_titulo || '—'}
                </p>
                <p className="font-mono text-gray-600 break-all">{selected.pagina_path}</p>
                {selected.pagina_url ? (
                  <p className="font-mono text-gray-400 break-all">{selected.pagina_url}</p>
                ) : null}
                {selected.modal_titulo ? (
                  <p className="pt-1 border-t border-gray-200 mt-1">
                    <span className="font-medium">Modal ativo:</span> {selected.modal_titulo}
                    {selected.modal_id ? (
                      <span className="font-mono text-gray-400"> ({selected.modal_id})</span>
                    ) : null}
                  </p>
                ) : (
                  <p className="text-gray-400 pt-1 border-t border-gray-200 mt-1">Sem modal ativo no reporte.</p>
                )}
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Usuário</p>
                <p>
                  {selected.user_nome} ({selected.user_email || '—'})
                </p>
                <p className="text-xs text-gray-500">
                  {selected.user_funcao || '—'}
                  {selected.empresa_nome ? ` · ${selected.empresa_nome}` : ''}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Mensagem</p>
                <p className="whitespace-pre-wrap text-gray-800 bg-white border rounded-lg px-3 py-2">
                  {selected.mensagem}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Notas de tratamento</label>
                <textarea
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  rows={3}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="O que foi feito / observação interna…"
                  disabled={salvando}
                />
              </div>
              {msg && (
                <p className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5">
                  {msg}
                </p>
              )}
            </div>
            <div className="px-4 py-3 border-t flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                disabled={salvando}
                onClick={() => atualizar('em_andamento')}
                className="px-3 py-2 text-xs border border-blue-300 text-blue-800 rounded-lg hover:bg-blue-50 disabled:opacity-50"
              >
                Em andamento
              </button>
              <button
                type="button"
                disabled={salvando}
                onClick={() => atualizar('resolvida')}
                className="px-3 py-2 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                Resolver
              </button>
              <button
                type="button"
                disabled={salvando}
                onClick={() => atualizar('descartada')}
                className="px-3 py-2 text-xs border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Descartar
              </button>
              <button
                type="button"
                disabled={salvando}
                onClick={() => atualizar(selected.status)}
                className="px-3 py-2 text-xs bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
              >
                {salvando ? 'Salvando…' : 'Salvar notas'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SolicitacoesSistemaPage;
